<template>
  <div class="relative h-12 w-full">
    <!-- Sections -->
    <div
      v-for="section in sections"
      :key="section"
      :class="[
        'absolute inset-0 transition-opacity duration-500 flex items-center',
        sectionClasses[section],
        { 'opacity-0': section !== currentSection, 'opacity-100': section === currentSection },
      ]"
    >
      <!-- Emojile Section -->
      <a v-if="section === 'emojile'" class="w-full flex items-center gap-2.5 z-3" href="/emojile">
        <div class="justify-start items-center flex">
          <OImage loading="lazy" src="/images/game/EmojileLogo.png" class="w-[90px] h-[34.50px] ml-1" />
        </div>
        <div class="flex flex-1 justify-around items-center">
          <div class="text-right text-white text-sm font-normal leading-[15px]">
            Günün
            <br />
            Sorusu
          </div>
          <div
            v-if="games.emojile"
            class="px-1.5 py-2 justify-center items-center gap-[3px] flex"
            style="background-color: rgba(255, 255, 255, 0.2)"
          >
            <div v-for="(i, idx) in games.emojile.question" :key="idx" class="u-center">
              <div
                class="rounded shadow border-[0.5px] text-20 leading-12 u-center p-4 w-8 h-8"
                style="background: rgba(255, 255, 255, 0.3)"
              >
                {{ i.emoji }}
              </div>
              <span v-if="idx < games.emojile.question.length - 1" class="text-md font-normal mx-0.5 text-white">+</span>
            </div>
            <div v-if="games.emojile.question.length < 3" class="u-center">
              <span class="text-md font-normal mx-0.5 text-white">=</span>
              <div
                class="rounded shadow border-[0.5px] text-20 text-white leading-12 u-center p-4 w-8 h-8"
                style="background: rgba(255, 255, 255, 0.3)"
              >
                ?
              </div>
            </div>
          </div>
          <OButton
            pure
            class="px-4 py-2 h-8 bg-[#2eb887] rounded-3xl u-center text-white text-[15px] font-semibold leading-snug tracking-tight"
            href="/emojile"
          >
            Oyna
          </OButton>
        </div>
      </a>

      <!-- Bilpop Section -->
      <a v-else-if="section === 'bilpop'" class="w-full flex items-center gap-2.5 z-3" href="/bilpop">
        <div class="justify-start items-center flex">
          <OImage loading="lazy" src="/images/game/BilpopLogo.png" class="w-[90px] h-[34.50px] ml-1" />
        </div>
        <div class="flex flex-1 justify-around items-center">
          <p class="text-right text-white text-sm font-normal leading-[15px]">
            Günün
            <br />
            Sorusu
          </p>
          <div
            class="h-12 relative u-center border-l-2 border-r-2 border-[#e98523] px-3 text-[#FF9513] text-sm font-medium leading-16 w-1/2"
          >
            <OIcon class="absolute -left-2" icon="Union-kesfet" />
            <p v-if="games.bilpop" class="line-clamp-2">{{ games.bilpop.question }}</p>
          </div>

          <OButton
            pure
            class="px-4 py-2 h-8 bg-[#2eb887] rounded-3xl u-center text-white text-[15px] font-semibold leading-snug tracking-tight"
            href="/bilpop"
          >
            Oyna
          </OButton>
        </div>
      </a>

      <!-- News Section -->
      <div v-else-if="section === 'news'" class="w-full flex items-center gap-2.5 px-3 z-3">
        <div class="text-lg font-black newsTextShadow leading-14 text-[#D9D9D9] flex-shrink-0 w-min">NELER OLUYOR?</div>
        <div class="w-1 h-1 bg-[#e94b4d] rounded-full flex-shrink-0"></div>
        <OLink v-if="article" class="flex-1 text-white text-xs font-normal leading-[19px] line-clamp-2" :href="getArticleUrl({ article })">
          {{ article.title }}
        </OLink>
      </div>
    </div>

    <!-- Progress Bar -->
    <OProgress :timeout="5000" class="bg-[#dbdbdb] h-1" fill-color="#9b9b9b" @handleNext="nextSection" />
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from '@nuxtjs/composition-api';
import { OButton, OImage, OIcon, OLink, OProgress } from '~/modules/common/components/ui';
import { getArticleUrl } from '~/modules/common/utils/routingUtils';

export default defineComponent({
  components: { OIcon, OImage, OButton, OLink, OProgress },
  props: { games: { type: Object, required: true }, articles: { type: Array, required: true } },
  setup(props) {
    const sections = ['emojile', 'bilpop', 'news'];
    const currentIndex = ref();
    const currentSection = ref();

    const sectionClasses = {
      emojile: 'bg-gradient-to-b from-[#00084d] to-[#2c4389]',
      bilpop: 'bg-gradient-to-b from-[#400577] to-[#6d2fbf]',
      news: 'bg-gradient-to-b from-[#121212] to-[#343434]',
    };

    const articleIndex = ref(0);

    const article = computed(() => {
      return props.articles[articleIndex.value];
    });

    const nextSection = () => {
      currentIndex.value = (currentIndex.value + 1) % sections.length;
      currentSection.value = sections[currentIndex.value];
      if (currentSection.value === 'news') articleIndex.value = (articleIndex.value + 1) % props.articles.length;
    };

    onMounted(() => {
      currentIndex.value = Math.floor(Math.random() * sections.length);
      currentSection.value = sections[currentIndex.value];
    });

    return {
      currentSection,
      sections,
      sectionClasses,
      nextSection,
      getArticleUrl,
      article,
    };
  },
});
</script>

<style>
.newsTextShadow {
  text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
}
</style>
